import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Plus, Trash2, Edit2, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function ShortcutsModal({ isOpen, onClose, shortcuts, onShortcutsChange }) {
  const [newName, setNewName] = useState("");
  const [newContent, setNewContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editContent, setEditContent] = useState("");

  const handleAddShortcut = async () => {
    if (!newName.trim() || !newContent.trim()) {
      toast.error("Please fill in both fields");
      return;
    }
    if (newName.length > 100) {
      toast.error("Shortcut name must be 100 characters or less");
      return;
    }
    if (newContent.length > 30000) {
      toast.error("Shortcut content must be 30,000 characters or less");
      return;
    }

    try {
      await axios.post(`${API}/shortcuts`, {
        name: newName.trim(),
        content: newContent.trim()
      }, { withCredentials: true });
      setNewName("");
      setNewContent("");
      onShortcutsChange();
      toast.success("Shortcut added!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add shortcut");
    }
  };

  const handleDeleteShortcut = async (id) => {
    try {
      await axios.delete(`${API}/shortcuts/${id}`, { withCredentials: true });
      onShortcutsChange();
      toast.success("Shortcut deleted");
    } catch (error) {
      toast.error("Failed to delete shortcut");
    }
  };

  const startEditing = (shortcut) => {
    setEditingId(shortcut.id);
    setEditName(shortcut.name);
    setEditContent(shortcut.content);
  };

  const handleUpdateShortcut = async () => {
    if (!editName.trim() || !editContent.trim()) {
      toast.error("Please fill in both fields");
      return;
    }
    if (editName.length > 100) {
      toast.error("Shortcut name must be 100 characters or less");
      return;
    }
    if (editContent.length > 30000) {
      toast.error("Shortcut content must be 30,000 characters or less");
      return;
    }

    try {
      await axios.put(`${API}/shortcuts/${editingId}`, {
        name: editName.trim(),
        content: editContent.trim()
      }, { withCredentials: true });
      setEditingId(null);
      onShortcutsChange();
      toast.success("Shortcut updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update shortcut");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
        data-testid="shortcuts-modal"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid #2d3748' }}
        >
          <h2 
            className="text-xl font-semibold"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#f5f5f5' }}
          >
            Shortcuts
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="close-shortcuts-btn"
            style={{ color: '#a0aec0' }}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Instructions */}
        <div className="px-6 py-3" style={{ backgroundColor: '#0f1419', borderBottom: '1px solid #2d3748' }}>
          <p className="text-sm" style={{ color: '#a0aec0' }}>
            Say <span className="font-bold text-purple-400">"shortcut [name]"</span> while recording to insert the saved content.
          </p>
        </div>

        {/* Add New Shortcut */}
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #2d3748' }}>
          {/* Shortcut Name */}
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#f5f5f5' }}>
              Shortcut Name
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value.slice(0, 100))}
              placeholder="Shortcut name"
              className="w-full px-4 py-2.5 rounded-lg border-none focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5' }}
              data-testid="shortcut-name-input"
              maxLength={100}
            />
          </div>

          {/* Shortcut Content - Large Textarea matching main message box */}
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#f5f5f5' }}>
              Content (text inserted)
            </label>
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value.slice(0, 30000))}
              placeholder="Place content here"
              className="w-full px-4 py-3 rounded-lg border-none focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm resize-none leading-relaxed"
              style={{ backgroundColor: '#0f1419', color: '#f5f5f5', minHeight: '140px' }}
              data-testid="shortcut-content-input"
              maxLength={30000}
              rows={6}
            />
          </div>

          <Button
            onClick={handleAddShortcut}
            data-testid="add-shortcut-btn"
            className="w-full rounded-lg py-2.5"
            style={{ backgroundColor: '#22c55e', color: '#fff' }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Save
          </Button>
        </div>

        {/* Shortcuts List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {shortcuts.length === 0 ? (
            <div className="text-center py-8">
              <p style={{ color: '#a0aec0' }}>No shortcuts yet</p>
              <p className="text-sm mt-1" style={{ color: '#718096' }}>
                Create your first shortcut above
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {shortcuts.map((shortcut) => (
                <div
                  key={shortcut.id}
                  className="rounded-xl p-4"
                  style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}
                  data-testid={`shortcut-item-${shortcut.id}`}
                >
                  {editingId === shortcut.id ? (
                    <div className="space-y-3">
                      {/* Edit Name */}
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#a0aec0' }}>
                          Name
                        </label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value.slice(0, 100))}
                          className="w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm"
                          style={{ backgroundColor: '#1a2332', color: '#f5f5f5' }}
                          maxLength={100}
                        />
                      </div>
                      
                      {/* Edit Content - Large Textarea */}
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#a0aec0' }}>
                          Content ({editContent.length.toLocaleString()}/30,000)
                        </label>
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value.slice(0, 30000))}
                          className="w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-purple-500 focus:outline-none text-sm resize-none leading-relaxed"
                          style={{ backgroundColor: '#1a2332', color: '#f5f5f5', minHeight: '140px' }}
                          maxLength={30000}
                          rows={6}
                        />
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingId(null)}
                          style={{ color: '#a0aec0' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleUpdateShortcut}
                          style={{ backgroundColor: '#22c55e', color: '#fff' }}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: '#718096' }}>Say:</span>
                          <span 
                            className="inline-block px-2.5 py-1 rounded text-sm font-mono font-medium"
                            style={{ backgroundColor: '#6366f1', color: '#fff' }}
                          >
                            shortcut {shortcut.name}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditing(shortcut)}
                            className="h-8 w-8 p-0"
                            style={{ color: '#a0aec0' }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteShortcut(shortcut.id)}
                            className="h-8 w-8 p-0"
                            style={{ color: '#ef4444' }}
                            data-testid={`delete-shortcut-${shortcut.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Display content with preserved line breaks */}
                      <div 
                        className="rounded-lg p-3"
                        style={{ backgroundColor: '#1a2332' }}
                      >
                        <pre 
                          className="text-sm break-words whitespace-pre-wrap font-sans"
                          style={{ color: '#f5f5f5', maxHeight: '120px', overflowY: 'auto' }}
                        >
                          {shortcut.content}
                        </pre>
                      </div>
                      <p className="text-xs mt-1.5" style={{ color: '#718096' }}>
                        {shortcut.content.length.toLocaleString()} characters
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
